import { isValidSuiAddress } from "@mysten/sui/utils";
import { KIOSK_OWNER_CAP } from "../types/index.js";
import {
  attachListingsAndPrices,
  attachLockedItems,
  attachObjects,
  extractKioskData,
  getAllDynamicFields,
  getAllObjects,
  getKioskObject
} from "../utils.js";
async function fetchKiosk(client, kioskId, pagination, options) {
  const data = await getAllDynamicFields(client, kioskId, pagination);
  const listings = [];
  const lockedItemIds = [];
  const kioskData = extractKioskData(data, listings, lockedItemIds, kioskId);
  const [kiosk, listingObjects, items] = await Promise.all([
    options.withKioskFields ? getKioskObject(client, kioskId) : Promise.resolve(void 0),
    options.withListingPrices ? getAllObjects(client, kioskData.listingIds, {
      showContent: true
    }) : Promise.resolve([]),
    options.withObjects ? getAllObjects(client, kioskData.itemIds, options.objectOptions || { showDisplay: true }) : Promise.resolve([])
  ]);
  if (options.withKioskFields) kioskData.kiosk = kiosk;
  attachListingsAndPrices(kioskData, listings, listingObjects);
  attachLockedItems(kioskData, lockedItemIds);
  attachObjects(
    kioskData,
    items.filter((x) => !!x.data).map((x) => x.data)
  );
  return {
    data: kioskData,
    nextCursor: null,
    hasNextPage: false
  };
}
const DEFAULT_PAGE_SIZE = 50;
const PERSON_KIOSK_CURSOR = "personal";
const OWNED_KIOSKS_CURSOR = "owned";
async function getOwnedKiosks(client, address, options) {
  if (!isValidSuiAddress(address)) {
    return {
      nextCursor: null,
      hasNextPage: false,
      kioskOwnerCaps: [],
      kioskIds: []
    };
  }
  const limit = options?.pagination?.limit ?? DEFAULT_PAGE_SIZE;
  const [cursorType, cursor] = options?.pagination?.cursor?.split(":") ?? [
    PERSON_KIOSK_CURSOR,
    null
  ];
  if (options?.personalKioskType && cursorType === PERSON_KIOSK_CURSOR) {
    const personalKioskResponse = formatOwnedKioskResponse(
      await client.getOwnedObjects({
        owner: address,
        filter: {
          StructType: options.personalKioskType
        },
        options: {
          showContent: true,
          showType: true
        },
        cursor,
        limit
      }),
      PERSON_KIOSK_CURSOR
    );
    if (personalKioskResponse.hasNextPage) {
      return personalKioskResponse;
    }
    const remainingLimit = limit - personalKioskResponse.kioskOwnerCaps.length;
    if (remainingLimit < 1) {
      return {
        nextCursor: `${OWNED_KIOSKS_CURSOR}:`,
        hasNextPage: true,
        kioskOwnerCaps: personalKioskResponse.kioskOwnerCaps,
        kioskIds: personalKioskResponse.kioskIds
      };
    }
    const ownedKiosksResponse = formatOwnedKioskResponse(
      await client.getOwnedObjects({
        owner: address,
        filter: {
          StructType: KIOSK_OWNER_CAP
        },
        options: {
          showContent: true,
          showType: true
        },
        limit: remainingLimit
      }),
      OWNED_KIOSKS_CURSOR
    );
    return {
      nextCursor: ownedKiosksResponse.nextCursor,
      hasNextPage: ownedKiosksResponse.hasNextPage,
      kioskOwnerCaps: [
        ...personalKioskResponse.kioskOwnerCaps,
        ...ownedKiosksResponse.kioskOwnerCaps
      ],
      kioskIds: [...personalKioskResponse.kioskIds, ...ownedKiosksResponse.kioskIds]
    };
  }
  return formatOwnedKioskResponse(
    await client.getOwnedObjects({
      owner: address,
      filter: {
        StructType: KIOSK_OWNER_CAP
      },
      options: {
        showContent: true,
        showType: true
      },
      // cursor might be an empty string if the number of personal kiosks was a multiple of the limit.
      cursor: cursor ? cursor : null,
      limit
    }),
    OWNED_KIOSKS_CURSOR
  );
}
function formatOwnedKioskResponse(response, cursorType) {
  const { data, hasNextPage, nextCursor } = response;
  const kioskIdList = data?.map((x) => {
    const fields = x.data?.content?.dataType === "moveObject" ? x.data.content.fields : null;
    return fields?.cap ? fields?.cap?.fields?.for : fields?.for;
  });
  const filteredData = data.filter((x) => "data" in x).map((x) => x.data);
  return {
    nextCursor: nextCursor ? `${cursorType}:${nextCursor}` : nextCursor,
    hasNextPage,
    kioskOwnerCaps: filteredData.map((x, idx) => ({
      isPersonal: x.type !== KIOSK_OWNER_CAP,
      digest: x.digest,
      version: x.version,
      objectId: x.objectId,
      kioskId: kioskIdList[idx]
    })),
    kioskIds: kioskIdList
  };
}
async function fetchKioskExtension(client, kioskId, extensionType) {
  const extension = await client.getDynamicFieldObject({
    parentId: kioskId,
    name: {
      type: `0x2::kiosk_extension::ExtensionKey<${extensionType}>`,
      value: {
        dummy_field: false
      }
    }
  });
  if (!extension.data) return null;
  const fields = extension?.data?.content?.fields?.value?.fields;
  return {
    objectId: extension.data.objectId,
    type: extensionType,
    isEnabled: fields?.is_enabled,
    permissions: fields?.permissions,
    storageId: fields?.storage?.fields?.id?.id,
    storageSize: fields?.storage?.fields?.size
  };
}
export {
  fetchKiosk,
  fetchKioskExtension,
  getOwnedKiosks
};
//# sourceMappingURL=kiosk.js.map
